/**
 * Nav de sections sticky pour la page dossier.
 *
 * - Desktop : nav sticky en haut de la zone de scroll, met en évidence la section
 *   active en fonction du scroll (IntersectionObserver). Clic = smooth scroll.
 * - Mobile : la même barre mais scroll-x, compact, avec badge d'alerte optionnel.
 *
 * Fournit une expérience "onglets sans onglets" : tout reste sur UNE page (Ctrl+F
 * fonctionne, print aussi) mais l'utilisateur peut sauter d'une section à l'autre.
 */

import { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * @param {Array} items - [{ id: 'section-xxx', label: 'Nom', icon: Component, badge?: number }]
 * @param {string} className
 */
export default function ProspectSectionNav({ items, className }) {
    const [active, setActive] = useState(items?.[0]?.id);
    const scrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        if (!items || items.length === 0) return;
        // Sélectionne l'entrée dont le top est le plus proche du haut de l'écran
        const compute = () => {
            if (scrollingRef.current) return;
            const stickyOffset = 80; // hauteur approx du header + nav sticky
            let bestId = items[0].id;
            let bestDist = Infinity;
            for (const it of items) {
                const el = document.getElementById(it.id);
                if (!el) continue;
                const top = el.getBoundingClientRect().top - stickyOffset;
                if (top <= 0 && Math.abs(top) < bestDist) {
                    bestDist = Math.abs(top);
                    bestId = it.id;
                }
            }
            setActive(bestId);
        };
        compute();
        const onScroll = () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(compute, 60);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, [items]);

    const jumpTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        // Freeze le compute pendant le scroll pour éviter les sauts d'active
        scrollingRef.current = true;
        setActive(id);
        const y = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setTimeout(() => { scrollingRef.current = false; }, 800);
    };

    if (!items || items.length === 0) return null;

    return (
        <div
            className={cn(
                // Mobile : sous le header ~56px. Desktop : sous le header ~80px (md:pt-20).
                "sticky top-[56px] md:top-20 z-30 -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8",
                "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm",
                className
            )}
            data-testid="prospect-section-nav"
        >
            <div className="px-3 sm:px-4 md:px-6 lg:px-8 overflow-x-auto scrollbar-hide">
                <div className="flex items-center gap-1 py-2 min-w-max">
                    {items.map((it) => {
                        const Icon = it.icon;
                        const isActive = active === it.id;
                        return (
                            <button
                                key={it.id}
                                type="button"
                                onClick={() => jumpTo(it.id)}
                                data-testid={`section-nav-${it.id}`}
                                className={cn(
                                    "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5",
                                    "text-xs sm:text-sm font-medium transition-all whitespace-nowrap border",
                                    isActive
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "text-slate-600 border-transparent hover:bg-slate-100 hover:text-slate-900"
                                )}
                            >
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                                {it.label}
                                {it.badge != null && it.badge > 0 && (
                                    <span className={cn(
                                        "ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1",
                                        "rounded-full text-[10px] font-bold leading-none",
                                        isActive ? "bg-white/20 text-white" : "bg-purple-600 text-white"
                                    )}>
                                        {it.badge > 9 ? '9+' : it.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
